import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCart, useCartSummary, useToast } from '../contexts';
import { Header, Footer } from '../components';
import Button from '../components/atoms/Button';
import PaymentForm from '../components/molecules/PaymentForm';
import CustomerInfoForm from '../components/molecules/CustomerInfoForm';
import { orderService } from '../services/orderService';
import { sendOrderConfirmationEmail, logEmailError } from '../services/orderEmailService';
import { cartService } from '../services/cartService';
import { cartEventListener } from '../services/cartEventListener';
import { CustomerInfo, NavLink } from '../types';
import './CheckoutPage.css';

interface CheckoutPageProps {
  className?: string;
}

export const CheckoutPage: React.FC<CheckoutPageProps> = ({ className = '' }) => {
  const navigate = useNavigate();
  const { cart, operations } = useCart();
  const { totalItems, subtotal, vat, vatPercentage, isInclusive, shipping, couponDiscount, total, isEmpty } = useCartSummary();
  const { showSuccess } = useToast();
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [currentStep, setCurrentStep] = useState<'customer' | 'payment'>('customer');
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);

  const navLinks: NavLink[] = [
    { text: 'Home', href: '/' },
    { text: 'Catalog', href: '/catalog' },
    { text: 'Contact', href: '#contact' }
  ];

  // Redirect if cart is empty
  useEffect(() => {
    if (isEmpty && !orderComplete) {
      navigate('/cart');
    }
  }, [isEmpty, orderComplete, navigate]);

  const handleCustomerInfoSubmit = async (data: CustomerInfo) => {
    setCustomerInfo(data);
    setCurrentStep('payment');
    setError(null);
    
    // Create payment intent when moving to payment step
    if (!paymentIntentId) {
      console.log('🔄 Creating payment intent for checkout...');
      try {
        const { stripeService } = await import('../services/stripeService');
        
        // First, get or create the customer to include in payment intent
        let customerId: string | undefined;
        try {
          customerId = await stripeService.createOrGetCustomer(data);
          console.log('✅ Customer ID for payment intent:', customerId);
        } catch (error) {
          console.warn('⚠️ Could not get customer ID, proceeding without it:', error);
        }
        
        const result = await stripeService.createPaymentIntent(
          total,
          'eur',
          {
            customer_email: data.email,
            customer_name: `${data.firstName} ${data.lastName}`,
            ...(customerId && { customer_id: customerId })
          }
        );
        
        if (result.success && result.paymentIntentId && result.clientSecret) {
          console.log('✅ Payment intent created successfully:', result.paymentIntentId);
          setPaymentIntentId(result.paymentIntentId);
          setClientSecret(result.clientSecret);
        } else {
          console.error('❌ Failed to create payment intent:', result.error);
          setError(result.error || 'Failed to initialize payment');
        }
      } catch (error) {
        console.error('❌ Error creating payment intent:', error);
        setError('Failed to initialize payment');
      }
    }
  };

  const handleCustomerInfoChange = async (customerInfo: Partial<CustomerInfo>) => {
    console.log('📝 CheckoutPage.handleCustomerInfoChange called:', {
      deliveryOption: customerInfo.deliveryOption,
      postalCode: customerInfo.address?.postalCode,
      hasAddress: !!customerInfo.address
    });

    // Update shipping when postal code or delivery option changes
    if (customerInfo.deliveryOption || customerInfo.address?.postalCode) {
      console.log('🔄 Updating shipping based on customer info change');
      try {
        console.log('🛒 Current cart before update:', cart);
        const updatedCart = await cartService.updateCartShipping(cart, {
          deliveryOption: customerInfo.deliveryOption,
          address: customerInfo.address
        });
        console.log('🛒 Updated cart after shipping calculation:', updatedCart);
        cartService.saveCart(updatedCart);
        
        // Manually trigger cart update notification to refresh the UI
        cartEventListener.triggerUpdate();
        console.log('✅ Cart updated successfully - shipping calculation preserved');
      } catch (error) {
        console.error('❌ Error updating shipping:', error);
      }
    } else {
      console.log('⏭️ Skipping shipping update - no delivery option or postal code');
    }
  };

  const handlePaymentSuccess = async (paymentIntentId: string) => {
    if (!customerInfo) {
      setError('Customer information is missing');
      return;
    }

    setIsProcessing(true);
    setError(null);
    
    try {
      console.log('✅ Payment confirmed by Stripe, creating order...');
      
      // Payment is already confirmed by PaymentForm, so we can directly create the order
      const order = await orderService.createOrder({
        customer: customerInfo,
        items: cart.items,
        deliveryOption: customerInfo.deliveryOption || 'collection',
        deliveryDate: cart.items[0]?.deliveryDate,
        deliveryAddress: customerInfo.deliveryOption === 'delivery' ? customerInfo.address : undefined,
        deliveryInstructions: customerInfo.deliveryInstructions,
        paymentMethodId: paymentIntentId
      });

      // Clear cart and show success
      await operations.clearCart();
      setOrderComplete(true);
      
      // Send order confirmation email
      try {
        console.log('📧 Sending order confirmation email...');
        const emailResult = await sendOrderConfirmationEmail(order);
        
        if (emailResult.success) {
          console.log('✅ Order confirmation email sent successfully');
        } else {
          console.error('❌ Failed to send order confirmation email:', emailResult.error);
          logEmailError(order, emailResult.error || 'Unknown email error');
        }
      } catch (emailError) {
        console.error('❌ Error sending order confirmation email:', emailError);
        logEmailError(order, emailError instanceof Error ? emailError.message : 'Unknown email error');
      }
      
      // Show success toast
      showSuccess(
        'Order Placed Successfully!',
        `Your order #${order.orderNumber} has been confirmed. You'll receive a confirmation email shortly.`
      );
      
      console.log('Order created successfully:', order);
      console.log('Setting orderComplete to true');
      console.log('Success message should now be visible');
      
      // Redirect to success page after delay (increased to 5 seconds to see the message)
      setTimeout(() => {
        console.log('Redirecting to success page...');
        navigate(`/checkout/success?order=${order.orderNumber}`);
      }, 5000);
    } catch (error) {
      console.error('Order creation failed:', error);
      setError(error instanceof Error ? error.message : 'Failed to create order. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePaymentError = (errorMessage: string) => {
    setError(errorMessage);
  };

  const handleBackToCustomer = () => {
    setCurrentStep('customer');
    setError(null);
  };


  const formatPrice = (price: number): string => {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  if (isEmpty && !orderComplete) {
    return null; // Will redirect
  }

  if (orderComplete) {
    return (
      <div className={`checkout-page ${className}`}>
        <Header navLinks={navLinks} />
        <div className="checkout-page__container">
          <div className="checkout-page__success">
            <div className="checkout-page__success-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h1 className="checkout-page__success-title">Order Placed Successfully!</h1>
            <p className="checkout-page__success-message">
              Thank you for your order! We'll start preparing your delicious treats right away.
              You'll receive a confirmation email shortly.
            </p>
            <p className="checkout-page__redirect-message">
              Redirecting to homepage in a few seconds...
            </p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App">
      <Header navLinks={navLinks} />
      
      <div className="checkout-page__container">
        <div className="checkout-page__header">
          <div className="checkout-page__breadcrumb">
            <Link to="/cart" className="checkout-page__back-link">
              ← Back to Cart
            </Link>
          </div>
          <h1 className="checkout-page__title">Checkout</h1>
          <p className="checkout-page__subtitle">
            Review your order and complete your purchase
          </p>
        </div>

        <div className="checkout-page__content">
          <div className="checkout-page__main">
            <div className="checkout-page__form-section">
              {error && (
                <div className="checkout-page__error">
                  <div className="checkout-page__error-icon">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <span>{error}</span>
                </div>
              )}

              {currentStep === 'customer' ? (
                <div className="checkout-page__customer-section">
                  <div className="checkout-page__section-header">
                    <h2>Customer Information</h2>
                    <p>Please provide your contact and delivery details</p>
                  </div>
                  <CustomerInfoForm
                    cartItems={cart.items}
                    onSubmit={handleCustomerInfoSubmit}
                    onInfoChange={handleCustomerInfoChange}
                    loading={isProcessing}
                    className="checkout-page__customer-form"
                  />
                </div>
              ) : (
                <div className="checkout-page__payment-section">
                  <div className="checkout-page__section-header">
                    <h2>Payment Information</h2>
                    <p>Complete your order with secure payment processing</p>
                  </div>
                  

                  <PaymentForm
                    amount={total}
                    currency="eur"
                    onPaymentSuccess={handlePaymentSuccess}
                    onPaymentError={handlePaymentError}
                    customerInfo={customerInfo || undefined}
                    paymentIntentId={paymentIntentId}
                    clientSecret={clientSecret}
                    className="checkout-page__payment-form"
                  />
                  

                  <div className="checkout-page__back-button">
                    <Button
                      variant="secondary"
                      onClick={handleBackToCustomer}
                      disabled={isProcessing}
                    >
                      ← Back to Customer Information
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <div className="checkout-page__order-summary">
              <div className="checkout-page__summary-card">
                <h2 className="checkout-page__summary-title">Order Summary</h2>
                {isInclusive && vat > 0 && (
                  <div className="checkout-page__vat-inclusive-notice">
                    <span className="checkout-page__vat-inclusive-text">
                      All prices include {vatPercentage}% VAT
                    </span>
                  </div>
                )}
                <div className="checkout-page__summary-details">
                  <div className="checkout-page__summary-line">
                    <span>Items ({totalItems})</span>
                    <span>{formatPrice(subtotal)}</span>
                  </div>
                  {vat > 0 && (
                    <div className="checkout-page__summary-line">
                      <span>
                        {isInclusive ? `VAT (${vatPercentage}%) - Included` : `VAT (${vatPercentage}%)`}
                      </span>
                      <span>{formatPrice(vat)}</span>
                    </div>
                  )}
                  <div className="checkout-page__summary-line">
                    <span>Shipping</span>
                    <span>
                      {shipping === 0 ? (
                        <span className="checkout-page__free-shipping">FREE</span>
                      ) : (
                        formatPrice(shipping)
                      )}
                    </span>
                  </div>
                  {couponDiscount > 0 && (
                    <div className="checkout-page__summary-line checkout-page__summary-line--discount">
                      <span>Discount</span>
                      <span className="checkout-page__discount-amount">
                        -{formatPrice(couponDiscount)}
                      </span>
                    </div>
                  )}
                  <div className="checkout-page__summary-line checkout-page__summary-line--total">
                    <span>Total</span>
                    <span>{formatPrice(total)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default CheckoutPage;